import dayjs from "@gtmhub/dayjs";
import { IInsightSelectorData } from "@gtmhub/insights";
import { IReduxTeam } from "@gtmhub/teams/models/models";
import { IdMap } from "@gtmhub/util";
import { Assignee, UnknownAssignee } from "@webapp/assignees/models/assignee.models";
import { isUnknownAssignee } from "@webapp/assignees/utils/assignee.utils";
import { AggregatedMentions } from "@webapp/comments/models/comments.models";
import { localize } from "@webapp/localization/utils/localization.utils";
import { MetricTypeSelectorOption } from "@webapp/okrs/metrics/components/metric-type-selector/metric-type-selector.models";
import { metricProperties } from "@webapp/okrs/utils/utils";
import { Session } from "@webapp/sessions/models/sessions.model";
import { getAllTeamMembersIncludingManager } from "@webapp/shared/utils/team";
import { Metric } from "../metrics/models/metric.models";

export function checkIsUpdateNoteValid(metric: Metric, value: string): boolean {
  if (metric.insightName !== "" || metric.sourceMetricId) {
    return !!value;
  }

  return true;
}

export function getMetricStatusInfoAriaLabel(metric: Metric): string {
  let statusInfoAriaLabel = "";

  if (metric.insightName !== "") {
    statusInfoAriaLabel = localize("dynamic_kr_update_automatically");
  } else if (metric.sourceMetricId) {
    statusInfoAriaLabel = localize("linked_kr_update_automatically");
  } else if (metric.cascadeType) {
    statusInfoAriaLabel = localize("cascaded_kr_update");
  }

  return statusInfoAriaLabel;
}

export function noDueDate(dueDate: string): boolean {
  return dueDate === "0001-01-01T00:00:00Z";
}

export function hardDateIsInTheFuture(dueDate: string, currentDate: string, unit: dayjs.OpUnitType = "hours"): boolean {
  return dayjs(new Date(dueDate)).diff(currentDate, unit) > 0;
}

export function noDueDateOrHardDateIsInTheFuture(dueDate: string, currentDate: string, unit: dayjs.OpUnitType = "hours"): boolean {
  return noDueDate(dueDate) || hardDateIsInTheFuture(dueDate, currentDate, unit);
}

export function dueDateIsInTheSessionTimePeriod(planningSession: Session, metric: Metric, unit: dayjs.OpUnitType = "hours"): boolean {
  return planningSession && dayjs(new Date(planningSession.end)).diff(metric.dueDate, unit) >= 0 && dayjs(new Date(metric.dueDate)).diff(planningSession.start, unit) > 0;
}

export function sessionIsArchivedOrInTheFutureOrOkrIsAbandoned(
  planningSession: Session,
  metric: Metric,
  minDate: Date,
  currentDate: string,
  unit: dayjs.OpUnitType
): boolean {
  return (
    dayjs(minDate).diff(currentDate, unit) > 0 ||
    (planningSession && planningSession.status === "closed") ||
    (metric.goalClosedStatus && metric.goalClosedStatus.status === "abandoned")
  );
}

export function getOwnerIds(ownerIds: string[], assignees: Record<string, Assignee>, teams: IdMap<IReduxTeam>): Set<string> {
  return ownerIds.reduce((ids: Set<string>, id: string) => {
    const owner = assignees[id];

    if (owner) {
      if (owner.type === "user") {
        ids.add(id);
      } else {
        const teamOwner = teams[id];
        if (teamOwner) {
          const allTeamMembers = getAllTeamMembersIncludingManager(teamOwner);
          allTeamMembers.forEach((member) => ids.add(member));
        }
      }
    }
    return ids;
  }, new Set());
}

export function generateRemoveAllMentionsAriaLabel(mentions: AggregatedMentions, assignees: Record<string, Assignee | UnknownAssignee>): string {
  const allMentions = [...mentions.removable, ...mentions.nonRemovable];
  const names = [];
  let removeAllMentionsAriaLabel = "";

  for (const mention of allMentions) {
    const assignee = assignees[mention];

    if (isUnknownAssignee(assignee)) {
      names.push(localize("deleted_user"));
    } else {
      names.push(assignee.name);
    }
  }

  if (names.length > 0) {
    removeAllMentionsAriaLabel = `${localize("remove_all")}. ${localize("people_notified")}: ${names.join(", ")}`;
  }

  return removeAllMentionsAriaLabel;
}

export const generateOptions = (metric: Partial<Metric>): MetricTypeSelectorOption[] => {
  const options: MetricTypeSelectorOption[] = [
    {
      emitValue: { manualType: "double", targetOperator: "at_least" },
      label: localize("should_increase_to"),
    },
  ];

  if (!metric.cascadeType) {
    options.push(
      {
        emitValue: { manualType: "double", targetOperator: "at_most" },
        label: localize("should_decrease_to"),
      },
      {
        emitValue: { manualType: "double", targetOperator: "should_stay_above" },
        label: localize("should_stay_above"),
      },
      {
        emitValue: { manualType: "double", targetOperator: "should_stay_below" },
        label: localize("should_stay_below"),
      }
    );

    if (metric.insightName === "") {
      options.push({
        emitValue: { manualType: "boolean", targetOperator: "at_least" },
        label: localize("achieved_or_not"),
        additionalLabel: "(100%/0%)",
      });
    }
  }

  return options;
};

export const isFieldPartOfWorkflowUtil = (fieldName: string, session: Session): boolean => {
  return !!session?.workflow?.editableKrFields?.includes(fieldName);
};

export const generateInsightSelectorData = (metric: Metric): IInsightSelectorData => {
  return {
    settings: metric.settings,
    insightName: metric.insightName,
    fieldName: metric.fieldName,
    initialValue: metric.initialValue,
  };
};

export const filterWorkflowSelectionProperties = (editableKrFields: string[]): string[] => {
  return metricProperties.filter((property) => editableKrFields.includes(property) || property === "customFields");
};
